import { resolveEquipmentProjectRate } from 'ontraccr-common/lib/ProjectBillingRate';
import React, {
  useMemo, useCallback, useState, useEffect, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Table,
  Select,
  Dropdown,
  Menu,
  Space,
} from 'antd';
import {
  ReloadOutlined,
  DownOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';
import {
  formatMillisecondsToHHMM,
} from '../../../../equipment/equipment.helpers';

import OnTraccrNumberInput from '../../../../common/inputs/OnTraccrNumberInput';
import TableDeleteButton from '../TableDeleteButton';
import OnTraccrTextInput from '../../../../common/inputs/OnTraccrTextInput';
import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import ManualEntryDatePicker from '../../../../clock/ManualEntry/ManualEntryDatePicker';

import EquipmentTableAddDrawer from './EquipmentTableAddDrawer';
import { getIdMap, sortByLocalCompare } from '../../../../helpers/helpers';
import { currencyParser, currencyFormatter } from '../../../../helpers/inputParsers';
import DisplayText from '../../../../common/text/DisplayText';
import {
  generateId,
  getHasColumnPermissions,
  getScopedTableCostcodes,
} from '../../../formHelpers';
import useCheckTableMaxRows from '../../../../common/hooks/useCheckTableMaxRows';
import { decorateEquipment, getEquipmentCurrentHoursSnapshot } from './EquipmentTable.helpers';
import TimeEntryTableTeamSelection from '../TimeEntryTable/TimeEntryTableTeamSelection';
import useToggle from '../../../../common/hooks/useToggle';
import {
  updateStackedDates,
  getCalculationTableColumn,
  getCustomTableColumn,
  LINE_NUMBER_COLUMN,
  createHandleReloadPreload,
  clearVendorOnCustomRows,
  clearCostcodesOnLinkedProjectChange,
} from '../formFieldsHelpers';
import { getUpdatedDateColumn, getColumnTitle } from '../FormFieldColumns';
import TableAttachmentCell from '../TableField/TableAttachmentCell';
import usePreloadHook from '../../../../common/hooks/usePreloadHook';
import { formatProjectLabelFromCompanySettings } from '../../../../projects/projectHelpers';
import Permissions from '../../../../auth/Permissions';
import { pendingTableColumn } from '../../../forms.constants';
import CostCodeTableSelector from '../CostCodeTable/CostCodeTableSelector';
import {
  getEligibleCategoriesForCostcode,
  getFormTableCostcodeFieldsFromIds,
  getFormTableCostcodeSelectValue,
  getFormTableCostcodeUpdateFields,
  formatFormTableCostcodeName,
} from '../../../../helpers/costcodeHelpers';
const renderCostColumn = ({
  key,
  val,
  record,
  isDisplay,
  onValueUpdated,
}) => {
  const formattedVal = currencyFormatter(val ?? 0);
  if (isDisplay) return formattedVal;
  return (
    <OnTraccrNumberInput
      precision={2}
      min={0}
      formatter={currencyFormatter}
      parser={currencyParser}
      value={val}
      onChange={(newVal) => onValueUpdated(record.id, key, newVal)}
    />
  );
};

const getColumnMap = ({
  isDisplay,
  onValueUpdated,
  requiredColumns,
  preventEdits,
  projectMap,
  activeProjects,
  settings,
  costcodeMap,
  vendors = {},
  equipment = {},
  divisions = [],
  costcodes,
  filterCostcode,
  onCostcodeUpdated,
  onOpenFile,
  previewProps,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  id: fieldId,
  responding,
  lockUsageColumns = false,
  customColumnNames = {},
  projectId,
  linkProjectCostCodes = false,
}) => {
  const onUpdatedDateChange = (value, record) => {
    onValueUpdated(record.id, 'updatedDate', value.join(', '));
  };
  const isReadOnly = isDisplay || preventEdits;
  const updatedDateColumn = getUpdatedDateColumn({
    title: customColumnNames.updatedDate || 'Date Updated',
    requiredColumns,
    onChange: onUpdatedDateChange,
    isDisplay: isDisplay || preventEdits,
  });
  const columns = {
    sourceForm: {
      title: customColumnNames.sourceForm || 'Source Form',
      dataIndex: 'sourceForm',
      width: 200,
      render: (val) => val,
    },
    name: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.name || 'Name'}</div>,
      dataIndex: 'name',
      width: 125,
      render: (val, record) => {
        if (isReadOnly) return val;
        return (
          <OnTraccrTextInput
            value={val}
            onChange={(e) => onValueUpdated(record.id, 'name', e.target.value)}
          />
        );
      },
    },
    code: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.code || 'ID'}</div>,
      dataIndex: 'code',
      width: 100,
      render: (val, record) => {
        if (isReadOnly) return val;
        return (
          <OnTraccrTextInput
            value={val}
            onChange={(e) => onValueUpdated(record.id, 'code', e.target.value)}
          />
        );
      },
    },
    hours: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.hours || 'Hours'}</div>,
      dataIndex: 'hours',
      width: 100,
      render: (hours, record) => {
        if (isDisplay || lockUsageColumns) return hours;
        return (
          <OnTraccrNumberInput
            value={hours}
            onChange={(newHours) => onValueUpdated(record.id, 'hours', newHours)}
          />
        );
      },
    },
    days: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.days || 'Days'}</div>,
      dataIndex: 'days',
      width: 100,
      render: (days, record) => {
        if (isDisplay || lockUsageColumns) return days;
        return (
          <OnTraccrNumberInput
            min={0}
            value={days}
            onChange={(v) => onValueUpdated(record.id, 'days', v)}
          />
        );
      },
    },
    weeks: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.weeks || 'Weeks'}</div>,
      dataIndex: 'weeks',
      width: 100,
      render: (weeks, record) => {
        if (isDisplay || lockUsageColumns) return weeks;
        return (
          <OnTraccrNumberInput
            min={0}
            value={weeks}
            onChange={(v) => onValueUpdated(record.id, 'weeks', v)}
          />
        );
      },
    },
    months: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.months || 'Months'}</div>,
      dataIndex: 'months',
      width: 100,
      render: (months, record) => {
        if (isDisplay || lockUsageColumns) return months;
        return (
          <OnTraccrNumberInput
            min={0}
            value={months}
            onChange={(v) => onValueUpdated(record.id, 'months', v)}
          />
        );
      },
    },
    currentHours: {
      title: customColumnNames.currentHours || 'Current Hours',
      dataIndex: 'currentHours',
      width: 100,
      render: (currentHours, record) => {
        const liveEquipment = !isDisplay && equipment[record?.equipmentId];
        const hours = liveEquipment
          ? getEquipmentCurrentHoursSnapshot(liveEquipment)
          : currentHours;
        return formatMillisecondsToHHMM(hours ?? 0);
      },
    },
    hourlyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.hourlyCost || 'Hourly Cost'}</div>,
      dataIndex: 'hourlyCost',
      width: 100,
      render: (hourlyCost, record) => renderCostColumn({
        key: 'hourlyCost',
        val: hourlyCost,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    hourlyBillingRate: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.hourlyBillingRate || 'Hourly Billing Rate'}</div>,
      dataIndex: 'hourlyBillingRate',
      width: 100,
      render: (hourlyBillingRate, record) => renderCostColumn({
        key: 'hourlyBillingRate',
        val: hourlyBillingRate,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    dailyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.dailyCost || 'Daily Cost'}</div>,
      dataIndex: 'dailyCost',
      width: 100,
      render: (dailyCost, record) => renderCostColumn({
        key: 'dailyCost',
        val: dailyCost,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    dailyBillingRate: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.dailyBillingRate || 'Daily Billing Rate'}</div>,
      dataIndex: 'dailyBillingRate',
      width: 100,
      render: (dailyBillingRate, record) => renderCostColumn({
        key: 'dailyBillingRate',
        val: dailyBillingRate,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    weeklyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>Weekly Cost</div>,
      dataIndex: 'weeklyCost',
      width: 100,
      render: (weeklyCost, record) => renderCostColumn({
        key: 'weeklyCost',
        val: weeklyCost,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    weeklyBillingRate: {
      title: <div className={requiredColumns && 'form-required-field'}>Weekly Billing Rate</div>,
      dataIndex: 'weeklyBillingRate',
      width: 100,
      render: (weeklyBillingRate, record) => renderCostColumn({
        key: 'weeklyBillingRate',
        val: weeklyBillingRate,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    monthlyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>Monthly Cost</div>,
      dataIndex: 'monthlyCost',
      width: 100,
      render: (monthlyCost, record) => renderCostColumn({
        key: 'monthlyCost',
        val: monthlyCost,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    monthlyBillingRate: {
      title: <div className={requiredColumns && 'form-required-field'}>Monthly Billing Rate</div>,
      dataIndex: 'monthlyBillingRate',
      width: 100,
      render: (monthlyBillingRate, record) => renderCostColumn({
        key: 'monthlyBillingRate',
        val: monthlyBillingRate,
        record,
        isDisplay: isDisplay || preventEdits,
        onValueUpdated,
      }),
    },
    totalHourlyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalHourlyCost || 'Total Hourly Cost'}</div>,
      dataIndex: 'totalHourlyCost',
      width: 100,
      render: (totalHourlyCost, record) => renderCostColumn({
        key: 'totalHourlyCost',
        val: totalHourlyCost,
        record,
        isDisplay: true,
      }),
    },
    totalDailyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalDailyCost || 'Total Daily Cost'}</div>,
      dataIndex: 'totalDailyCost',
      width: 100,
      render: (totalDailyCost, record) => renderCostColumn({
        key: 'totalDailyCost',
        val: totalDailyCost,
        record,
        isDisplay: true,
      }),
    },
    totalWeeklyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalWeeklyCost || 'Total Weekly Cost'}</div>,
      dataIndex: 'totalWeeklyCost',
      width: 100,
      render: (totalWeeklyCost, record) => renderCostColumn({
        key: 'totalWeeklyCost',
        val: totalWeeklyCost,
        record,
        isDisplay: true,
      }),
    },
    totalMonthlyCost: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalMonthlyCost || 'Total Monthly Cost'}</div>,
      dataIndex: 'totalMonthlyCost',
      width: 100,
      render: (totalMonthlyCost, record) => renderCostColumn({
        key: 'totalMonthlyCost',
        val: totalMonthlyCost,
        record,
        isDisplay: true,
      }),
    },
    totalHourlyBilling: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalHourlyBilling || 'Total Hourly Billing'}</div>,
      dataIndex: 'totalHourlyBilling',
      width: 100,
      render: (totalHourlyBilling, record) => renderCostColumn({
        key: 'totalHourlyBilling',
        val: totalHourlyBilling,
        record,
        isDisplay: true,
      }),
    },
    totalDailyBilling: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalDailyBilling || 'Total Daily Billing'}</div>,
      dataIndex: 'totalDailyBilling',
      width: 100,
      render: (totalDailyBilling, record) => renderCostColumn({
        key: 'totalDailyBilling',
        val: totalDailyBilling,
        record,
        isDisplay: true,
      }),
    },
    totalWeeklyBilling: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalWeeklyBilling || 'Total Weekly Billing'}</div>,
      dataIndex: 'totalWeeklyBilling',
      width: 100,
      render: (totalWeeklyBilling, record) => renderCostColumn({
        key: 'totalWeeklyBilling',
        val: totalWeeklyBilling,
        record,
        isDisplay: true,
      }),
    },
    totalMonthlyBilling: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalMonthlyBilling || 'Total Monthly Billing'}</div>,
      dataIndex: 'totalMonthlyBilling',
      width: 100,
      render: (totalMonthlyBilling, record) => renderCostColumn({
        key: 'totalMonthlyBilling',
        val: totalMonthlyBilling,
        record,
        isDisplay: true,
      }),
    },
    totalCost: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalCost || 'Total Cost'}</div>, // HARBOUR-6839: Renamed from Total Hourly Cost
      dataIndex: 'totalCost',
      width: 100,
      render: (totalCost, record) => renderCostColumn({
        key: 'totalCost',
        val: totalCost,
        record,
        isDisplay: true,
      }),
    },
    totalBilling: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.totalBilling || 'Total Billing'}</div>, // HARBOUR-6839: Renamed from Total Hourly Billing
      dataIndex: 'totalBilling',
      width: 100,
      render: (totalBilling, record) => renderCostColumn({
        key: 'totalBilling',
        val: totalBilling,
        record,
        isDisplay: true,
      }),
    },
    addedDate: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.addedDate || 'Date Added'}</div>,
      dataIndex: 'addedDate',
      width: 250,
      render: (date, record) => {
        if (isDisplay || preventEdits) return date;

        return (
          <ManualEntryDatePicker
            value={date}
            onChange={(newDate) => onValueUpdated(record.id, 'addedDate', newDate)}
          />
        );
      },
    },
    updatedDate: updatedDateColumn,
    currentProjectId: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.currentProjectId || 'Location'}</div>,
      dataIndex: 'currentProjectId',
      width: 250,
      render: (projectId) => {
        const selectedProject = projectMap[projectId];
        const projectLabel = formatProjectLabelFromCompanySettings(
          { name: selectedProject?.name, number: selectedProject?.number, settings },
        );
        return projectLabel;
      },
    },
    toProjectId: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.toProjectId || 'To Location'}</div>,
      dataIndex: 'toProjectId',
      width: 250,
      render: (projectId, record) => {
        const selectedProject = projectMap[projectId];
        const projectLabel = formatProjectLabelFromCompanySettings(
          { name: selectedProject?.name, number: selectedProject?.number, settings },
        );
        if (isDisplay) return projectLabel;
        const selectedDivisions = record?.divisionIds || [];
        const projectOptions = activeProjects
          .filter((project) => (
            selectedDivisions.includes(project.divisionId)
            && project.id !== record.currentProjectId))
          .map((project) => (
            {
              label: formatProjectLabelFromCompanySettings({
                name: project.name, number: project.number, settings,
              }),
              value: project.id,
            }));

        return (
          <Select
            showSearch
            allowClear
            style={{ minWidth: 250 }}
            placeholder="Select a Project"
            options={projectOptions}
            onChange={(newProjectId) => onValueUpdated(record.id, 'toProjectId', newProjectId)}
            value={record.toProjectId}
            optionFilterProp="label"
          />
        );
      },
    },
    notes: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.notes || 'Notes' }),
      dataIndex: 'notes',
      width: 250,
      render: (notes, record) => {
        if (isReadOnly) return notes;
        return (
          <OnTraccrTextInput
            key={record.id}
            value={notes}
            onChange={(e) => {
              const {
                target: {
                  value,
                } = {},
              } = e;
              onValueUpdated(record.id, 'notes', value);
            }}
            textarea
          />
        );
      },
    },
    costcode: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.costcode || 'Cost Code' }),
      dataIndex: 'costcodeId',
      width: 250,
      render: (_, record) => {
        if (isReadOnly) {
          return record.costcode || costcodeMap[record.costcodeId]?.name || '';
        }

        return (
          <CostCodeTableSelector
            projectId={linkProjectCostCodes ? projectId : undefined}
            onCostcodeUpdated={onCostcodeUpdated}
            value={getFormTableCostcodeSelectValue(record, { costcodeMap })}
            entryId={record.id}
            costcodes={costcodes}
            costcodeMap={costcodeMap}
            categoryFilter="equipmentAndLabor"
            filterCostcode={filterCostcode}
            globalOnly={!linkProjectCostCodes || !projectId}
            style={{ width: '100%' }}
          />
        );
      },
    },
    vendor: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.vendor || 'Vendor' }),
      dataIndex: 'vendorId',
      width: 150,
      render: (vendorId, record) => {
        if (isReadOnly) {
          const vendor = vendors[vendorId];
          return vendor ? vendor.name : '';
        }

        const equipmentItem = equipment[record.equipmentId];
        const equipmentDivisionIds = equipmentItem?.divisionIds;
        const divisionIds = (record.divisionIds || equipmentDivisionIds || divisions);

        const recordVendorId = vendorId === undefined ? (equipmentItem?.vendorId || null) : vendorId;

        const equipmentDivisionIdSet = new Set(divisionIds);

        const filteredVendors = Object.values(vendors).filter((vendor) =>
          vendor.active &&
          (!equipmentDivisionIdSet.size || vendor.divisionIds?.some(divId => equipmentDivisionIdSet.has(divId)))
        );

        const sortedVendors = filteredVendors.toSorted(sortByLocalCompare('name'));

        const vendorOptions = sortedVendors.map((vendor) => (
          {
            label: vendor.name,
            value: vendor.id,
          }
        ));

        return (
          <Select
            value={recordVendorId}
            onChange={(value) => onValueUpdated(record.id, 'vendorId', value)}
            optionFilterProp="label"
            allowClear
            showSearch
            placeholder="Select a Vendor"
            style={{ width: 150 }}
            options={vendorOptions}
          />
        );
      },
    },
    bucketPath: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.bucketPath || 'Bucket Path' }),
      dataIndex: 'bucketPath',
      width: 250,
      render: (bucketPath, record) => {
        if (isReadOnly) return bucketPath;
        return <OnTraccrTextInput value={bucketPath} onChange={(e) => onValueUpdated(record.id, 'bucketPath', e.target.value)} />;
      },
    },
    mileage: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.mileage || 'Mileage'}</div>,
      dataIndex: 'mileage',
      width: 100,
      render: (currentMileage, record) => {
        if (isDisplay) return currentMileage;
        return (
          <OnTraccrNumberInput
            min={0}
            value={currentMileage}
            onChange={(newCurrentMileage) => onValueUpdated(record.id, 'mileage', newCurrentMileage)}
          />
        );
      },
    },
    currentMileage: {
      title: customColumnNames.currentMileage || 'Current Mileage',
      dataIndex: 'currentMileage',
      width: 100,
      render: (currentMileage, record) => {
        const liveEquipment = !isDisplay && equipment[record?.equipmentId];
        const mileage = liveEquipment
          ? (liveEquipment.currentMileage ?? currentMileage)
          : currentMileage;
        return mileage ?? 0;
      },
    },
    newHoursThreshold: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.newHoursThreshold || 'Hours Threshold'}</div>,
      dataIndex: 'newHoursThreshold',
      width: 100,
      render: (newHoursThreshold, record) => {
        if (isDisplay) return newHoursThreshold;
        return (
          <OnTraccrNumberInput
            min={0}
            value={newHoursThreshold}
            onChange={(hoursThreshold) => onValueUpdated(record.id, 'newHoursThreshold', hoursThreshold)}
          />
        );
      },
    },
    newMileageThreshold: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.newMileageThreshold || 'Mileage Threshold'}</div>,
      dataIndex: 'newMileageThreshold',
      width: 100,
      render: (newMileageThreshold, record) => {
        if (isDisplay) return newMileageThreshold;
        return (
          <OnTraccrNumberInput
            min={0}
            value={newMileageThreshold}
            onChange={(mileageThreshold) => onValueUpdated(record.id, 'newMileageThreshold', mileageThreshold)}
          />
        );
      },
    },
    attachments: {
      title: getColumnTitle({ requiredColumns, title: customColumnNames.attachments || 'Attachments' }),
      dataIndex: 'attachments',
      width: 220,
      render: (attachments, record) => (
        <TableAttachmentCell
          value={attachments}
          isDisplay={isReadOnly}
          onChange={(next) => onValueUpdated(record.id, 'attachments', next)}
          onOpenFile={onOpenFile}
          setCurrFileFieldId={setCurrFileFieldId}
          setCurrAttachmentRowId={setCurrAttachmentRowId}
          setOpenFileIndex={setOpenFileIndex}
          setCurrFieldId={setCurrFieldId}
          attachmentCellId={fieldId && record?.id ? `${fieldId}-${record.id}` : undefined}
          id={fieldId}
          rowId={record?.id}
          previewProps={previewProps}
          responding={responding}
        />
      ),
    },
  };

  return columns;
};

const getColumns = ({
  onDelete,
  columns,
  isDisplay,
  onValueUpdated,
  requiredColumns,
  preventEdits,
  projectMap,
  activeProjects,
  settings,
  columnPermissions,
  userPositionId,
  hasPendingTableData,
  costcodeMap,
  vendors = {},
  equipment = {},
  divisions = [],
  costcodes,
  filterCostcode,
  onCostcodeUpdated,
  onOpenFile,
  previewProps,
  setCurrAttachmentFieldId,
  setOpenFileIndex,
  setCurrFieldId,
  id,
  responding,
  projectId,
  linkProjectCostCodes = false,
  hideDelete = false,
  lockUsageColumns = false,
  responses,
}) => {
  const cols = [LINE_NUMBER_COLUMN];

  // Create custom column names map from columns prop
  const customColumnNames = columns.reduce((acc, col) => {
    acc[col.key] = col.name;
    return acc;
  }, {});

  const colMap = getColumnMap({
    isDisplay,
    onValueUpdated,
    requiredColumns,
    preventEdits,
    projectMap,
    activeProjects,
    settings,
    costcodeMap,
    vendors,
    equipment,
    divisions,
    costcodes,
    filterCostcode,
    onCostcodeUpdated,
    onOpenFile,
    previewProps,
    setCurrAttachmentFieldId,
    setOpenFileIndex,
    setCurrFieldId,
    id,
    responding,
    customColumnNames,
    projectId,
    linkProjectCostCodes,
    lockUsageColumns,
  });

  if (hasPendingTableData) {
    cols.push(pendingTableColumn);
  }

  columns.forEach((col) => {
    if (!getHasColumnPermissions({
      columnPermissions,
      column: col,
      userPositionId,
      userId: Permissions.id,
    })) {
      return;
    }
    if (col.key in colMap) {
      cols.push(colMap[col.key]);
    } else if (col.isCustomText) {
      cols.push(getCustomTableColumn(col, {
        width: 100,
        isDisplay,
        preventEdits,
        requiredColumns,
        responses,
        onChange: (record, value) => onValueUpdated(record.id, col.key, value),
      }));
    } else if (col.isCalculation) {
      cols.push(getCalculationTableColumn(col, {
        isDisplay,
      }));
    }
  });
  if (!isDisplay && !hideDelete) {
    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <TableDeleteButton onConfirm={() => onDelete(record.id)} />
      ),
    });
  }

  return cols;
};

function EquipmentTablePreview({
  title = null,
  equipment = [],
  projectBillingRateProjectId,
  projectId,
  columns = [],
  previewProps = {},
  setPreviewProps,
  isDisplay,
  id,
  setResponses,
  responses = {},
  responding = false,
  configProps = {},
  showCondensedView,
  divisions = [],
  costcodes = [],
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
}) {
  const {
    requiredColumns,
    preventEdits,
    hideAddFromTeam,
    linkedTimeEntryTable,
    linkedSourceEquipmentTable,
    syncHoursFromLinkedSource = false,
    columnPermissions = {},
    hideAddNewButton,
    linkProjectCostCodes = false,
    linkProjectCostCodesDropdown,
  } = configProps ?? {};
  const values = previewProps.values || []; // For Responses
  const {
    selected: previewSelected,
  } = previewProps;
  const {
    [id]: {
      values: responseSelected,
    } = {},
  } = responses;

  const rawSelected = responding ? responseSelected : previewSelected;

  const [prevTimeEntryResponses, setPrevTimeEntryResponses] = useState();
  const [prevEquipmentResponses, setPrevEquipmentResponses] = useState();

  const teams = useSelector((state) => state.teams.teams);
  const projects = useSelector((state) => state.projects.projects);
  const vendors = useSelector((state) => state.vendors.vendors);
  const projectBillingRatesByProjectId = useSelector((state) => state.projectBillingRates.projectRates);
  const projectRatesForBilling = projectBillingRatesByProjectId?.[projectBillingRateProjectId];

  const { settings = {} } = useSelector((state) => state.settings.company);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const filterCostcode = useCallback((cc) => {
    if (!cc) return false;
    const divSet = new Set(divisions);
    return cc.active
      && divSet.has(cc.divisionId)
      && getEligibleCategoriesForCostcode(cc, categoriesIdMap, { filter: 'equipmentAndLabor' }).length > 0;
  }, [divisions, categoriesIdMap]);
  const phasedCostcodes = useSelector((state) => state.costcodes.phases);
  const phaseMap = useMemo(() => getIdMap(phasedCostcodes), [phasedCostcodes]);
  const positionNames = useSelector((state) => state.settings.positionNames);
  const users = useSelector((state) => state.users.users);

  const activeProjects = useMemo(() => projects.filter((project) => project.active), [projects]);
  const projectMap = useMemo(() => getIdMap(activeProjects), [activeProjects]);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userMap = useMemo(() => getIdMap(users), [users]);

  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const {
    costcodes: tableCostcodes,
    linkedProjectId: linkedProjectIdForCostCodes,
  } = useMemo(() => getScopedTableCostcodes({
    costcodes,
    linkProjectCostCodes,
    linkProjectCostCodesDropdown,
    responses,
  }), [costcodes, linkProjectCostCodes, linkProjectCostCodesDropdown, responses]);

  const costcodeMap = useMemo(() => getIdMap(tableCostcodes), [tableCostcodes]);

  const parsedEquipment = useMemo(() => (
    equipment.map((eq) => {
      const newEquipment = { ...eq };
      const { costcodeId } = newEquipment;

      if (costcodeId) {
        const costcode = costcodeMap?.[costcodeId] ?? {};
        const {
          hourlyWage,
          dailyWage,
          hourlyBillingRate,
          dailyBillingRate,
          name,
        } = costcode;
        if (hourlyWage) newEquipment.hourlyCost = hourlyWage;
        if (dailyWage) newEquipment.dailyCost = dailyWage;
        if (hourlyBillingRate) newEquipment.hourlyBillingRate = hourlyBillingRate;
        if (dailyBillingRate) newEquipment.dailyBillingRate = dailyBillingRate;
        if (name) newEquipment.costcode = name;
      }
      const projectRates = resolveEquipmentProjectRate(
        projectRatesForBilling,
        eq.id,
        eq.equipmentTypeId,
      );
      if (projectRates) {
        Object.entries(projectRates).forEach(([key, value]) => {
          if (value !== null) newEquipment[key] = value;
        });
      }
      return newEquipment;
    })
  ), [equipment, costcodeMap, projectRatesForBilling]);

  const equipmentMap = useMemo(() => (
    getIdMap(parsedEquipment)
  ), [parsedEquipment]);

  const selected = useMemo(() => {
    const newId = generateId();
    return rawSelected?.map((s, idx) => (
      // Backwards Compatability - HARBOUR-4193
      s.equipmentId ? s : { ...s, equipmentId: s.id, id: newId + idx }
    )) ?? [];
  }, [rawSelected]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const showDrawer = useCallback(() => setDrawerOpen(true), []);
  const hideDrawer = useCallback(() => setDrawerOpen(false), []);

  const { toggle: toggleTeamSelect, isToggled: showTeamSelect } = useToggle();

  const { triggerPreload } = usePreloadHook({
    responses,
    id,
    setResponses,
    columns,
    responding,
    configProps,
    type: 'Equipment',
    setLoading,
    divisions,
    costcodeMap,
  });

  useEffect(() => {
    if (
      isDisplay
      || !responding
      || !linkedTimeEntryTable
      || (!!linkedSourceEquipmentTable && syncHoursFromLinkedSource)
      || (
        prevTimeEntryResponses === responses[linkedTimeEntryTable]
        && prevEquipmentResponses === responses[id]
      )
    ) return;

    const relevantUserIds = new Set(rawSelected?.map((entry) => entry.userId) ?? []);

    const relevantTimeEntryValues = (
      responses[linkedTimeEntryTable]?.values ?? []
    )?.filter((entry) => relevantUserIds.has(entry.userId));

    const userHoursMap = relevantTimeEntryValues.reduce((acc, entry) => {
      const {
        userId,
        work_hours: workHours,
      } = entry;
      if (!acc[userId]) acc[userId] = 0;
      acc[userId] += parseFloat(workHours, 10) || 0;
      return acc;
    }, {});

    const newEquipmentResponse = {
      ...(responses[id]),
      values: rawSelected?.map((eq) => decorateEquipment({
        ...eq,
        hours: eq.didTouchHours ? eq.hours : (userHoursMap[eq.userId]?.toFixed(2) || 0),
      })) ?? [],
      columns,
    };

    setPrevTimeEntryResponses(responses[linkedTimeEntryTable]);
    setPrevEquipmentResponses(newEquipmentResponse);
    setResponses({
      ...responses,
      [id]: newEquipmentResponse,
    });
  }, [
    equipmentMap,
    isDisplay,
    responding,
    responses,
    linkedTimeEntryTable,
    rawSelected,
    id,
    prevTimeEntryResponses,
    prevEquipmentResponses,
    linkedSourceEquipmentTable,
    syncHoursFromLinkedSource,
  ]);

  const updateSelected = useCallback((newSelected) => {
    if (responding) {
      setResponses((prev = {}) => ({
        ...prev,
        [id]: {
          ...(prev[id]),
          values: newSelected.map((eq) => decorateEquipment(eq)),
          columns,
        },
      }));
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected.map((eq) => decorateEquipment(eq)),
      });
    }
  }, [
    responding,
    id,
    columns,
    previewProps,
    setResponses,
    setPreviewProps,
  ]);

  useEffect(() => {
    const { changed, nextSelected } = clearVendorOnCustomRows({ selected, vendors, divisions });
    if (changed) updateSelected(nextSelected);
  }, [divisions, selected, vendors, updateSelected]);

  const prevLinkedProjectIdForCostCodes = useRef(linkedProjectIdForCostCodes);
  useEffect(() => {
    const {
      changed,
      nextSelected,
      nextPrevLinkedProjectId,
    } = clearCostcodesOnLinkedProjectChange({
      selected,
      linkedProjectId: linkedProjectIdForCostCodes,
      prevLinkedProjectId: prevLinkedProjectIdForCostCodes.current,
      linkProjectCostCodes,
      responding,
      isDisplay,
    });
    prevLinkedProjectIdForCostCodes.current = nextPrevLinkedProjectId;
    if (changed) updateSelected(nextSelected);
  }, [
    linkedProjectIdForCostCodes,
    linkProjectCostCodes,
    responding,
    isDisplay,
    selected,
    updateSelected,
  ]);

  const onSubmitChanges = useCallback((newSelected) => {
    const finalValues = [...selected];
    const newId = generateId();

    newSelected.forEach((s, idx) => {
      const snapshotHours = getEquipmentCurrentHoursSnapshot(s);
      const currentProject = projectMap[s.currentProjectId];
      const projectName = formatProjectLabelFromCompanySettings(
        { name: currentProject?.name, number: currentProject?.number, settings },
      );
      finalValues.push({
        ...s,
        currentHours: snapshotHours,
        equipmentId: s.id,
        currentProjectName: projectName,
        id: newId + idx,
        addedDate: TaskHelpers.formatDate(DateTime.local().toMillis()),
        updatedDate: DateTime.local().toISO(),
        vendorId: s.vendorId,
        vendor: vendors[s.vendorId]?.name ?? '',
        ...getFormTableCostcodeFieldsFromIds({
          costcodeId: s.costcodeId,
          phaseId: s.phaseId,
          costcodeMap,
          categoriesIdMap,
          categoryFilter: 'equipmentAndLabor',
        }),
      });
    });
    updateSelected(finalValues);
    hideDrawer();
  }, [
    updateSelected,
    hideDrawer,
    selected,
    costcodeMap,
    categoriesIdMap,
    projectMap,
    settings,
    vendors,
  ]);

  const onDelete = useCallback((itemId) => {
    const newSelected = selected.filter((item) => item.id !== itemId);
    updateSelected(newSelected);
  }, [updateSelected, selected]);

  const onValueUpdated = useCallback((rowId, keyOrUpdates, value) => {
    const updates = typeof keyOrUpdates === 'object' && keyOrUpdates !== null && value === undefined
      ? keyOrUpdates
      : { [keyOrUpdates]: value };

    const newSelected = selected.map((row) => {
      if (row.id !== rowId) return row;
      const newRow = {
        ...row,
        ...updates,
      };
      const hasChanges = Object.keys(updates).some((key) => updates[key] !== row[key]);
      if (hasChanges) {
        newRow.isPreloaded = false;
      }
      if (!('updatedDate' in updates)) {
        newRow.updatedDate = updateStackedDates(row.updatedDate);
      }

      if ('hours' in updates) {
        newRow.didTouchHours = true;
      }
      if ('toProjectId' in updates) {
        const currentProject = projectMap[updates.toProjectId];
        const projectName = formatProjectLabelFromCompanySettings(
          { name: currentProject?.name, number: currentProject?.number, settings },
        );
        newRow.toProjectName = projectName;
      }
      if ('costcodeId' in updates && !('costcode' in updates)) {
        const costcode = costcodeMap[updates.costcodeId];
        newRow.costcode = costcode?.name;
      }
      if ('vendorId' in updates) {
        newRow.vendor = vendors[updates.vendorId]?.name ?? '';
      }
      return newRow;
    });
    updateSelected(newSelected);
  }, [updateSelected, selected, costcodeMap, projectMap, settings, vendors]);

  const onCostcodeUpdated = useCallback((rowId, selectedVal) => {
    onValueUpdated(rowId, getFormTableCostcodeUpdateFields({
      selectedVal,
      phaseMap,
      costcodeMap,
      categoriesIdMap,
      categoryFilter: 'equipmentAndLabor',
      formatCostcode: formatFormTableCostcodeName,
    }));
  }, [onValueUpdated, phaseMap, costcodeMap, categoriesIdMap]);

  const onAddFromTeam = useCallback((teamId) => {
    const selectedTeam = teams.find(({ id: tId }) => tId === teamId);
    const { equipment: teamEquipment = [] } = selectedTeam ?? {};
    if (!teamEquipment.length) {
      toggleTeamSelect();
      return;
    }

    const finalValues = [...selected];
    teamEquipment.forEach((eqId, idx) => {
      const fullEquipment = equipmentMap[eqId];
      if (!fullEquipment) return;
      const newId = generateId();
      const snapshotHours = getEquipmentCurrentHoursSnapshot(fullEquipment);
      finalValues.push({
        ...fullEquipment,
        currentHours: snapshotHours,
        equipmentId: fullEquipment.id,
        id: newId + idx,
        addedDate: TaskHelpers.formatDate(DateTime.local().toMillis()),
        updatedDate: DateTime.local().toISO(),
        vendorId: fullEquipment.vendorId,
        vendor: vendors[fullEquipment.vendorId]?.name ?? '',
        ...getFormTableCostcodeFieldsFromIds({
          costcodeId: fullEquipment.costcodeId,
          phaseId: fullEquipment.phaseId,
          costcodeMap,
          categoriesIdMap,
          categoryFilter: 'equipmentAndLabor',
        }),
      });
    });
    updateSelected(finalValues);
    toggleTeamSelect();
  }, [
    selected,
    equipmentMap,
    teams,
    updateSelected,
    toggleTeamSelect,
    costcodeMap,
    categoriesIdMap,
    vendors,
  ]);
  const onAddNew = useCallback(() => {
    const newSelected = selected.concat([{
      id: generateId(),
      equipmentId: generateId(),
      currentHours: 0,
      currentMileage: 0,
      isCustom: true,
      addedDate: TaskHelpers.formatDate(DateTime.local().toMillis()),
      updatedDate: DateTime.local().toISO(),
    }]);
    updateSelected(newSelected);
  }, [selected, updateSelected]);

  const dataSource = useMemo(() => (
    isDisplay && !responding ? values : selected
  ), [isDisplay, responding, values, selected]);

  const hasPendingTableData = useMemo(
    () => dataSource.some((eq) => eq.pendingTableValue),
    [dataSource],
  );

  const tableColumns = useMemo(() => (
    getColumns({
      onDelete,
      onValueUpdated,
      columns,
      isDisplay: isDisplay || loading,
      requiredColumns,
      preventEdits,
      projectMap,
      activeProjects,
      settings,
      columnPermissions,
      userPositionId,
      hasPendingTableData,
      costcodeMap,
      vendors,
      equipment: equipmentMap,
      divisions,
      costcodes: tableCostcodes,
      filterCostcode,
      onCostcodeUpdated,
      onOpenFile,
      previewProps,
      setCurrFileFieldId,
      setCurrAttachmentRowId,
      setOpenFileIndex,
      setCurrFieldId,
      id,
      responding,
      projectId: linkedProjectIdForCostCodes,
      linkProjectCostCodes,
      hideDelete: !!linkedSourceEquipmentTable,
      lockUsageColumns: !!linkedSourceEquipmentTable && syncHoursFromLinkedSource,
      responses,
    })
  ), [
    columns,
    isDisplay,
    onDelete,
    onValueUpdated,
    requiredColumns,
    preventEdits,
    projectMap,
    activeProjects,
    settings,
    columnPermissions,
    userPositionId,
    hasPendingTableData,
    costcodeMap,
    tableCostcodes,
    filterCostcode,
    onCostcodeUpdated,
    onOpenFile,
    previewProps,
    setCurrFileFieldId,
    setCurrAttachmentRowId,
    setOpenFileIndex,
    setCurrFieldId,
    id,
    responding,
    vendors,
    equipmentMap,
    divisions,
    linkedProjectIdForCostCodes,
    linkProjectCostCodes,
    linkedSourceEquipmentTable,
    syncHoursFromLinkedSource,
    loading,
    responses,
  ]);

  const {
    maxExistingAllowed,
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: dataSource?.length,
  });

  const handleReloadPreload = useCallback(
    createHandleReloadPreload({
      responses,
      id,
      onReload: triggerPreload,
    }),
    [triggerPreload, responses, id],
  );

  const { preloadExistingEntries = false } = configProps ?? {};
  const showReloadButton = preloadExistingEntries && responding && !isDisplay;

  return (
    <>
      <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
        <Row justify='space-between' style={{ width: '100%', marginBottom: 10 }}>
          <Row>
            {title}
          </Row>
          {!isDisplay && !loading && (
            <Space gutter={10}>
              {showReloadButton && (
                <OnTraccrButton
                  title="Reload Preloaded Data"
                  onClick={handleReloadPreload}
                  icon={<ReloadOutlined />}
                />
              )}
              {!linkedSourceEquipmentTable && (
                <Dropdown
                  placement="bottomRight"
                  trigger={['click']}
                  overlay={(
                    <Menu
                      onClick={({ key }) => {
                        switch (key) {
                          case 'addFromTeam':
                            toggleTeamSelect();
                            break;
                          case 'addEquipment':
                            showDrawer();
                            break;
                          case 'addNew':
                            onAddNew();
                            break;
                        }
                      }}
                    >
                      {!hideAddNewButton && (
                        <Menu.Item key="addNew" disabled={!shouldAddButtonBeEnabled}>
                          Add Row
                        </Menu.Item>
                      )}
                      <Menu.Item key="addEquipment" disabled={!shouldAddButtonBeEnabled}>
                        Add Equipment
                      </Menu.Item>
                      {!hideAddFromTeam && (
                        <Menu.Item key="addFromTeam">
                          Add from Team
                        </Menu.Item>
                      )}
                    </Menu>
                  )}
                >
                  <OnTraccrButton
                    title="Add"
                    icon={<DownOutlined />}
                  />
                </Dropdown>
              )}
            </Space>
          )}
        </Row>
        { !showCondensedView || dataSource?.length ? (
          <Table
            className='form-table-preview'
            style={{ width: '100%', overflow: 'auto' }}
            columns={tableColumns}
            size="small"
            pagination={false}
            dataSource={dataSource}
            loading={loading}
            rowClassName={(record) => (record.pendingTableValue ? 'pending-table-value' : '')}
          />
        ) : (
          <DisplayText title="No Equipment Selected" style={{ marginBottom: 0 }} />
        )}
      </Row>
      {!isDisplay && !loading && (
        <>
          <EquipmentTableAddDrawer
            visible={drawerOpen}
            onClose={hideDrawer}
            onSubmit={onSubmitChanges}
            equipment={parsedEquipment}
            maxExistingAllowed={maxExistingAllowed}
            configProps={configProps}
          />
          <TimeEntryTableTeamSelection
            divisions={divisions}
            visible={showTeamSelect}
            onClose={toggleTeamSelect}
            onAdd={onAddFromTeam}
          />
        </>
      )}
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
EquipmentTablePreview.propTypes = {
  title: PropTypes.node,
  equipment: PropTypes.array,
  projectBillingRateProjectId: PropTypes.string,
  columns: PropTypes.array,
  previewProps: PropTypes.object,
  setPreviewProps: PropTypes.func.isRequired,
  isDisplay: PropTypes.bool,
  id: PropTypes.string.isRequired,
  setResponses: PropTypes.func.isRequired,
  responses: PropTypes.object,
  responding: PropTypes.bool,
  configProps: PropTypes.shape({
    requiredColumns: PropTypes.bool,
    preventEdits: PropTypes.bool,
  }),
  showCondensedView: PropTypes.bool,
  divisions: PropTypes.array,
  costcodes: PropTypes.array,
};

EquipmentTablePreview.defaultProps = {
  title: null,
  equipment: [],
  columns: [],
  previewProps: {},
  responses: {},
  isDisplay: false,
  responding: false,
  configProps: {},
  showCondensedView: false,
  divisions: [],
  costcodes: [],
};

export default EquipmentTablePreview;
